import { createTheme, darken, Grow, lighten } from "@mui/material";
import React from "react";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Grow ref={ref} {...props} />;
});

export const isStaging = process.env.REACT_APP_ENV === "staging";

// #047BC4 original

const primaryColor = {
  light: isStaging ? "#E8711B" : "#1B72E8",
  dark: isStaging ? "#FFB74D" : "#9ACBFF",
};

const elevation1 = {
  light: "#F0F4F8",
  dark: "#27282C",
};

const theme = (mode) =>
  createTheme({
    unstable_strictMode: true,
    typography: {
      fontSize: 13.5,
      fontFamily: "'Figtree', sans-serif",
      allVariants: {
        letterSpacing: -0.15,
        fontVariantLigatures: "no-contextual",
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
        fontSmoothing: "auto",
        textRendering: "optimizeLegibility",
        fontOpticalSizing: "auto",
        fontStyle: "normal",
        fontWeight: "500",
      },
      button: {
        fontWeight: "600",
        textTransform: "none",
        fontSize: ".925rem",
      },
    },
    components: {
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: 23,
            paddingBlock: 16,
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            fontSize: ".9rem",
            fontWeight: "500",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: mode === "dark" ? "#27282C" : "#FFF",
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            "&& .MuiTouchRipple-rippleVisible": {
              animationDuration: "200ms",
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            "&& .MuiTouchRipple-rippleVisible": {
              animationDuration: "200ms",
            },
          },
        },
        defaultProps: {
          TabIndicatorProps: {
            sx: {
              height: 3,
              borderTopLeftRadius: 100,
              borderTopRightRadius: 100,
            },
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            "&& .MuiTouchRipple-rippleVisible": {
              animationDuration: "150ms",
            },
            paddingInline: "18px",
            borderRadius: 100,
          },
          outlinedPrimary: {
            borderColor:
              mode === "dark" ? "rgb(255,255,255,.2)" : "rgb(0,0,0,.2)",
            ":hover": {
              borderColor:
                mode === "dark" ? "rgb(255,255,255,.1)" : "rgb(0,0,0,.3)",
            },
          },
          outlinedInherit: {
            borderColor:
              mode === "dark" ? "rgb(255,255,255,.2)" : "rgb(0,0,0,.2)",
            ":hover": {
              borderColor:
                mode === "dark" ? "rgb(255,255,255,.1)" : "rgb(0,0,0,.3)",
            },
          },
        },
      },
      MuiIconButton: {
        defaultProps: {
          onDragStart: (e) => {
            e.stopPropagation();
            e.preventDefault();
          },
          TouchRippleProps: {
            onDragStart: (e) => {
              e.stopPropagation();
              e.preventDefault();
            },
          },
        },
        styleOverrides: {
          root: {
            "&& .MuiTouchRipple-rippleVisible": {
              animationDuration: "50ms",
            },
          },
        },
      },
      MuiMenuItem: {
        defaultProps: {
          "data-disableselect": true,
        },
        styleOverrides: {
          root: {
            fontSize: ".95rem",
            fontWeight: "600",
            "&& .MuiTouchRipple-rippleVisible": {
              animationDuration: "120ms",
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            "&& .MuiTouchRipple-rippleVisible": {
              animationDuration: "200ms",
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            "&& .MuiTouchRipple-rippleVisible": {
              animationDuration: "200ms",
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            "&& .MuiTouchRipple-rippleVisible": {
              animationDuration: "200ms",
            },
          },
        },
      },
      MuiListItemButton: {
        defaultProps: {
          "data-disableselect": true,
        },
        styleOverrides: {
          root: {
            "&& .MuiTouchRipple-rippleVisible": {
              animationDuration: "200ms",
            },
          },
        },
      },
      MuiCardActionArea: {
        styleOverrides: {
          root: {
            "&& .MuiTouchRipple-rippleVisible": {
              animationDuration: "300ms",
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            "&& .MuiTouchRipple-rippleVisible": {
              animationDuration: "200ms",
            },
          },
        },
      },
      MuiTooltip: {
        defaultProps: {
          componentsProps: {
            popper: {
              "data-disableselect": true,
              sx: {
                boxShadow: 0,
              },
            },
          },
        },
        styleOverrides: {
          arrow: {
            color: "#474747",
          },
          tooltip: {
            borderRadius: 3,
            backgroundColor: "#474747",
            fontSize: ".82rem",
            fontWeight: "600",
          },
        },
      },
      MuiLink: {
        defaultProps: {
          sx: {
            cursor: "pointer",
          },
        },
      },
      MuiMenu: {
        defaultProps: {
          transitionDuration: 100,
          disableScrollLock: true,
        },
      },
      MuiPopover: {
        defaultProps: {
          transitionDuration: 50,
          disableScrollLock: true,
        },
      },
      MuiDialog: {
        defaultProps: {
          transitionDuration: 200,
          TransitionComponent: Transition,
          disableScrollLock: true,
          slotProps: {
            backdrop: {
              sx: {
                bgcolor: "rgba(0,0,0,.3)",
              },
            },
          },
        },
        styleOverrides: {
          paper: {
            boxShadow: "0px 1px 6px rgba(0,0,0,.2)",
            backgroundColor: lighten(elevation1.light, 0.2),
            borderRadius: "28px",
          },
        },
      },
      MuiDrawer: {
        defaultProps: {
          elevation: 3,
          transitionDuration: 150,
          slotProps: {
            backdrop: {
              sx: {
                bgcolor: "rgba(0,0,0,.3)",
              },
            },
          },
        },
      },
      MuiSnackbarContent: {
        styleOverrides: {
          root: {
            backgroundColor: mode === "dark" ? "#272932" : "#323232",
            color: "#fff",
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          inputRoot: {
            "& .MuiInputBase-input": {
              fontWeight: "500",
            },
          },
          option: {
            fontWeight: "500",
          },
        },
        defaultProps: {
          loadingText: "Carregando...",
          noOptionsText: "Nada encontrado",
          openText: "Abrir",
          closeText: "Fechar",
          clearText: "Limpar",
          slotProps: {
            paper: {
              sx: {
                boxShadow: 5,
              },
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            transition: "none",
            "& .MuiInputBase-input": {
              fontWeight: "500",
            },
            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: mode === "dark" ? "#FFF" : "#666666",
              cursor: "not-allowed",
            },
            "& .MuiInputLabel-root.Mui-disabled": {
              WebkitTextFillColor: mode === "dark" ? "#FFF" : "#666666",
            },
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            transition: "none",
            backgroundColor:
              mode === "dark" ? "#3E4146" : darken("#F0F4F8", 0.02),
            "&:hover": {
              backgroundColor:
                mode === "dark" ? "#3E4146" : lighten("#DDE3E9", 0.25),
            },
            "&.Mui-focused": {
              backgroundColor:
                mode === "dark" ? "#3E4146" : lighten("#DDE3E9", 0.25),
            },
          },
        },
      },
      MuiDialogTitle: {
        defaultProps: {
          component: "div",
          display: "flex",
          alignItems: "center",
        },
        styleOverrides: {
          root: {
            display: "flex",
            alignItems: "center",
            zIndex: 10,
            minHeight: 60,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: ".95rem",
            fontWeight: "500",
            color: mode === "dark" ? "#FFF" : "#272727",
            opacity: mode === "dark" ? 1 : 0.8,
          },
          shrink: {
            fontSize: "1rem",
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontWeight: "500",
          },
        },
      },
    },
    palette: {
      primary: {
        main: primaryColor[mode],
      },
      primaryDark: {
        main: "#AEDFF2",
      },
      background: {
        default: mode === "dark" ? "#202124" : "#FFF",
        paper: mode === "dark" ? "#202124" : "#FFF",
      },
      elevation1: {
        main: elevation1[mode],
      },
      elevation2: {
        main: "#D0E0F3",
      },
      backgroundPrimary: {
        main: mode === "dark" ? "#27282C" : "#FFF",
      },
      success: {
        main: mode === "dark" ? "#72C472" : "#2F7C31",
      },
      secondary: {
        main: mode === "dark" ? "#72C472" : "#0A1B3D",
      },

      error: {
        main: mode === "dark" ? "#FFB4AB" : "#C5221F",
      },
      text: {
        primary: "#1F1F1F",
      },
      surfaceVariant: {
        main: mode === "dark" ? "#202124" : "#EFF4FA",
      },
      capition: {
        main: "#B1AEAE",
      },
      cleanWhite: {
        main: mode === "dark" ? "#32333C" : "#FFFFFF",
      },
      warning: {
        main: "#de7915",
      },
      warningContainer: {
        main: "#FF9800",
      },
      action: {
        active: mode === "dark" ? "#FFF" : "#5F6367",
      },
      mode,
    },
  });

export default theme;
